/**
 * 직원 관리(EA-30) — 직원 목록과 권한 이름·상태·임시 비밀번호(EA-31~EA-34), 직원 만들기(EA-32).
 *
 * 계약: XM-13(목록)·XM-14(만들기)·XM-15(상태)·XM-16(권한 이름)·XM-17(임시 비밀번호)·XM-118(잠금 풀기), 고르개는
 * XM-19(권한 이름 목록). 모두 **최고관리자만**이다(계약 7절 머리말 — 키가 아니다).
 *
 * 잠금 풀기: 사장님 2026-10-01 질문 39 「1」 — 직원 목록의 잠긴 직원 줄에 [잠금 풀기]를 두어 최고관리자가 한 번 눌러
 * 풀게 한다(앞에는 상태를 비활성 → 활성으로 두 번 바꿔야 풀렸다). 한 번 누르면 바로 보낸다(확인 창 없음 — 말씀
 * 「한 번 눌러」). 최고관리자 계정 잠금은 서버 명령(거래소백엔드 scripts/unlock-super-admin.sh)으로 푼다.
 *
 * 결정: 사장님 「최초 최고관리자는 시스템에서 생성하고 거기서 직원을 생성하고 메뉴
 * 버튼별권한 지정하는걸로 하자」·「실상 직원에게 관리자라는 권한을지정만하고 내부적으로
 * 관리자는 어떤페이지 어떤버튼 설정하는거지 이해되나?」.
 *
 * ⚠ **최고관리자 줄에는 버튼이 없다.** 서버도 막는다(계약 XM-15~XM-17).
 *
 * ⚠ **임시 비밀번호는 이 웹을 지나지 않는다.** 서버가 메일로만 보낸다(XM-14·XM-17).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createStaff, listPermissionNames, listStaff, resendTemporaryPassword, setStaffPermissionName,
  setStaffStatus, unlockStaff,
} from '@/api/admin'
import { errorMessage } from '@/api/errors'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { Badge, Button, Card, Field, Labeled, Maybe, Notice, Select, TD, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { ADMIN_STATUS_LABEL, type AdminStatus, type StaffRow } from '@/api/types'

export function StaffPage() {
  const { superAdmin } = useSession()
  const q = useQuery({ queryKey: ['staff'], queryFn: listStaff })
  const rows = q.data?.admins ?? []

  return (
    <Card title="Staff">
      <p className="mb-4 text-xs text-slate-500">
        Staff accounts and their permission names. Each staff member gets one permission name. The super admin
        account cannot be changed.
      </p>
      {superAdmin && <div className="mb-4"><CreateStaffButton /></div>}
      <QueryState isPending={q.isPending} error={q.error}
        empty={rows.length === 0} emptyText="There are no staff members.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>ID</th><th className={TH}>Email</th>
            <th className={TH}>Permission name</th><th className={TH}>Status</th>
            <th className={TH}>Last sign-in</th><th className={TH}>Created at</th>
            <th className={TH}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.adminId}>
                <td className={TD}>
                  {s.loginId}
                  {s.superAdmin && <Badge tone="ok">Super admin</Badge>}
                  {s.setupRequired && <Badge tone="warn">Before first setup</Badge>}
                  {/* ⚠ PIN 이 없으면 그 계정은 일을 못 한다 — 붉게 말한다. */}
                  {!s.hasPin && <Badge tone="stop">No PIN</Badge>}
                  {s.loginLocked && <Badge tone="stop">Sign-in locked</Badge>}
                </td>
                <td className={TD}>{s.email}</td>
                <td className={TD}>
                  {s.superAdmin ? <Maybe value={null} /> : <Maybe value={s.permissionName?.name ?? null} />}
                </td>
                <td className={TD}><Labeled labels={ADMIN_STATUS_LABEL} code={s.status} /></td>
                <td className={TD}><Time ms={s.lastLoginAt} /></td>
                <td className={TD}><Time ms={s.createdAt} /></td>
                <td className={TD}>{superAdmin && !s.superAdmin && <StaffButtons row={s} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </Card>
  )
}

type Mode = 'permission' | 'status' | 'password'

function StaffButtons({ row }: { row: StaffRow }) {
  const [mode, setMode] = useState<Mode | null>(null)
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {row.loginLocked && <UnlockButton row={row} />}
        <Button tone="plain" onClick={() => setMode('permission')}>Change permission name</Button>
        <Button tone="plain" onClick={() => setMode('status')}>Change status</Button>
        <Button tone="plain" onClick={() => setMode('password')}>Resend temporary password</Button>
      </div>
      {mode === 'permission' && <PermissionNameDialog row={row} onClose={() => setMode(null)} />}
      {mode === 'status' && <StatusDialog row={row} onClose={() => setMode(null)} />}
      {mode === 'password' && <ResendDialog row={row} onClose={() => setMode(null)} />}
    </>
  )
}

/**
 * 잠긴 직원 줄의 [잠금 풀기](XM-118) — 한 번 누르면 바로 보낸다. 끝나면 Flash 와 목록 다시 읽기. 실패는 빨간
 * Flash. PIN 에 막히면 다른 쓰기처럼 기억했다가 키패드를 지난 뒤 한 번 다시 보낸다.
 */
function UnlockButton({ row }: { row: StaffRow }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function unlock() {
    const send = async () => {
      const r = await unlockStaff(row.adminId)
      setFlash({
        tone: 'ok',
        text: r.unlocked
          ? `Unlocked ${row.loginId}.`
          : `${row.loginId} was not locked. Nothing changed.`,
      })
      await qc.invalidateQueries({ queryKey: ['staff'] })
    }
    await submitWrite(w, 'Unlock', send, () => undefined, {
      onError: (err) => setFlash({ tone: 'stop', text: errorMessage(err) }),
    })
  }

  return (
    <Button tone="plain" onClick={() => { void unlock() }} disabled={w.busy}>
      {w.busy ? 'Unlocking…' : 'Unlock'}
    </Button>
  )
}

/** 권한 이름 고르개의 목록(XM-19). */
function usePermissionNameOptions() {
  const q = useQuery({ queryKey: ['permission-names'], queryFn: listPermissionNames })
  const options = (q.data?.names ?? []).map((n) => ({ value: String(n.id), label: n.name }))
  return { q, options, nameOf: (id: string) => options.find((o) => o.value === id)?.label ?? id }
}

/** EA-31 — 권한 이름 변경(XM-16). 레거시 '업무 변경' 자리. */
function PermissionNameDialog({ row, onClose }: { row: StaffRow; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const names = usePermissionNameOptions()
  const [nameId, setNameId] = useState(row.permissionName ? String(row.permissionName.id) : '')

  async function submit() {
    const chosen = names.nameOf(nameId)
    const send = async () => {
      await setStaffPermissionName(row.adminId, Number(nameId))
      setFlash({ tone: 'ok', text: `Changed the permission name of ${row.loginId} to "${chosen}".` })
      await qc.invalidateQueries({ queryKey: ['staff'] })
      await qc.invalidateQueries({ queryKey: ['permission-names'] })
    }
    await submitWrite(w, 'Change permission name', send, onClose)
  }

  return (
    <Confirm open title={`Permission name of ${row.loginId}`} action="Apply"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={nameId === ''}>
      <div className="flex flex-col gap-3">
        <QueryState isPending={names.q.isPending} error={names.q.error}>
          <Select label="Permission name" value={nameId} onChange={setNameId}
            options={[...(nameId === '' ? [{ value: '', label: 'Choose one' }] : []), ...names.options]} />
        </QueryState>
        <p className="text-xs text-slate-500">Applies right away from this staff member's next request.</p>
      </div>
    </Confirm>
  )
}

/** EA-32 — 직원 만들기(XM-14). */
function CreateStaffButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button onClick={() => setOpen(true)}>Create staff</Button>
      {open && <CreateStaffDialog onClose={() => setOpen(false)} />}
    </>
  )
}

function CreateStaffDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const names = usePermissionNameOptions()
  const [loginId, setLoginId] = useState('')
  const [email, setEmail] = useState('')
  const [nameId, setNameId] = useState('')

  async function submit() {
    const id = loginId.trim()
    const mail = email.trim()
    const send = async () => {
      await createStaff(id, mail, Number(nameId))
      setFlash({ tone: 'ok', text: `Created the account ${id} and sent a temporary password to ${mail}.` })
      await qc.invalidateQueries({ queryKey: ['staff'] })
      await qc.invalidateQueries({ queryKey: ['permission-names'] })
    }
    await submitWrite(w, 'Create staff', send, onClose)
  }

  return (
    <Confirm open title="Create staff" action="Create"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={loginId.trim() === '' || email.trim() === '' || nameId === ''}>
      <div className="flex flex-col gap-3">
        <Field label="ID" value={loginId} maxLength={64} autoComplete="off"
          onChange={(e) => setLoginId(e.target.value)} />
        {/* type=email 을 쓰지 않는다 — 브라우저 형식 검사 말풍선(컴퓨터 언어) 대신 서버 `invalid_email` 의 우리 글. */}
        <Field label="Email" inputMode="email" value={email} maxLength={254} autoComplete="off"
          onChange={(e) => setEmail(e.target.value)} />
        <QueryState isPending={names.q.isPending} error={names.q.error}>
          <Select label="Permission name" value={nameId} onChange={setNameId}
            options={[{ value: '', label: 'Choose one' }, ...names.options]} />
        </QueryState>
        <p className="text-xs text-slate-500">
          The temporary password is sent by mail only. The staff member sets a password and a 6-digit PIN at the
          first sign-in.
        </p>
      </div>
    </Confirm>
  )
}

/** EA-33 — 직원 상태 변경(XM-15). */
function StatusDialog({ row, onClose }: { row: StaffRow; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const [status, setStatus] = useState<AdminStatus>(row.status)
  const stopping = status !== 'ACTIVE'

  async function submit() {
    const send = async () => {
      await setStaffStatus(row.adminId, status)
      setFlash({ tone: 'ok', text: `Changed the status of ${row.loginId} to ${ADMIN_STATUS_LABEL[status]}.` })
      await qc.invalidateQueries({ queryKey: ['staff'] })
    }
    await submitWrite(w, 'Change staff status', send, onClose)
  }

  // ⚠ 비활성·정지는 이 창에서 다시 활성으로 돌릴 수 있다(계정은 그대로) — 「되돌릴 수 없습니다」를
  // 붙이지 않는다(밤 시험 X-04).
  return (
    <Confirm open title={`Status of ${row.loginId}`} action="Apply" danger={stopping} irreversible={false}
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={status === row.status}>
      <div className="flex flex-col gap-3">
        <fieldset className="flex flex-col gap-2">
          {(Object.keys(ADMIN_STATUS_LABEL) as AdminStatus[]).map((s) => (
            <label key={s} className="flex items-center gap-2">
              <input type="radio" name="staff-status" checked={status === s} onChange={() => setStatus(s)} />
              <span>{ADMIN_STATUS_LABEL[s]}</span>
            </label>
          ))}
        </fieldset>
        {stopping && (
          <p className="text-xs text-slate-500">
            While inactive or suspended, all viewing and actions of this staff member are refused. The account
            stays.
          </p>
        )}
      </div>
    </Confirm>
  )
}

/** EA-34 — 임시 비밀번호 다시 보내기(XM-17). */
function ResendDialog({ row, onClose }: { row: StaffRow; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      await resendTemporaryPassword(row.adminId)
      setFlash({ tone: 'ok', text: `Sent a new temporary password to ${row.loginId}.` })
      await qc.invalidateQueries({ queryKey: ['staff'] })
    }
    await submitWrite(w, 'Resend temporary password', send, onClose)
  }

  return (
    <Confirm open title="Resend temporary password" action="Send" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <Notice tone="warn">
        Sends a new temporary password to {row.email}. The previous temporary password stops working, this staff
        member's sessions end and the PIN is cleared. The password and the PIN are set again at the next sign-in.
      </Notice>
    </Confirm>
  )
}
